import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, Globe } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { Photo } from '../../types/photo';
import { usePhotoContext } from '../../contexts/PhotoContext';

interface PhotoItemProps {
  photo: Photo;
  index: number;
  isDragging: boolean;
}

export const PhotoItem: React.FC<PhotoItemProps> = ({
  photo,
  index,
  isDragging
}) => {
  const { toggleForWeb, removePhoto } = usePhotoContext();
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isItemDragging,
  } = useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative aspect-square rounded-lg overflow-hidden group",
        "border-2",
        photo.forWeb ? "border-primary-500" : "border-gray-200 dark:border-gray-700",
        isItemDragging && "z-10 shadow-lg",
        isDragging && !isItemDragging && "opacity-50"
      )}
    >
      <img
        src={photo.preview}
        alt={`Photo ${index}`}
        className="w-full h-full object-cover"
        onError={(e) => {
          console.error('Erreur de chargement de la photo:', photo.preview, photo);
          // Essayer de charger l'URL du serveur si disponible
          if (photo.serverUrl && photo.preview !== photo.serverUrl) {
            e.currentTarget.src = photo.serverUrl;
          }
        }}
      />
      
      {/* Badge de publication */}
      <div className={cn(
        "absolute top-2 left-2 px-2 py-1 rounded text-sm transition-colors",
        photo.forWeb 
          ? "bg-primary-500 text-white"
          : "bg-gray-500/50 text-white"
      )}>
        {photo.forWeb ? "Publié" : "Non publié"}
      </div>

      {/* Overlay avec les actions */}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="absolute top-2 right-2 flex gap-2">
          <button
            onClick={() => toggleForWeb(photo.id)}
            className={cn(
              "p-1.5 rounded-full transition-colors",
              photo.forWeb 
                ? "bg-primary-500 text-white hover:bg-primary-600" 
                : "bg-gray-500/50 text-white hover:bg-gray-500"
            )}
            title={photo.forWeb ? "Retirer du web" : "Publier sur le web"}
          >
            <Globe className="h-4 w-4" />
          </button>
          <button
            onClick={() => removePhoto(photo.id)}
            className="p-1.5 rounded-full bg-red-500/50 text-white hover:bg-red-500"
            title="Supprimer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Poignée de glisser-déposer */}
        <div
          {...attributes}
          {...listeners}
          className="absolute bottom-2 left-1/2 -translate-x-1/2 cursor-move p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70"
          title="Déplacer"
        >
          <GripVertical className="h-4 w-4" />
        </div>
      </div>

      {/* Barre de progression */}
      {photo.progress < 100 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
          <div
            className="h-full bg-primary-500 transition-all duration-200"
            style={{ width: `${photo.progress}%` }}
          />
        </div>
      )}
    </div>
  );
};